// Copyright (c) 2026 Michael F. Kamprath
//
// Simplified Chinese (zh-CN) UI string dictionary.
//
// Translations sourced from the legacy iOS app's
// counting-on-ewe/zh-Hans.lproj/Localizable.strings (compiled 2011) where
// the legacy app had an equivalent key.  Web-only keys (anything not in
// the legacy strings file: Support / PayPal, Family cross-promo, Privacy
// disclosure, Credits, About prose) are left as English placeholders.
// Untranslated keys fall back to English via the ./index.js resolver, so
// omitting a key here is equivalent to leaving the placeholder.
//
// Where the legacy iOS string is a slightly different phrasing than the
// web copy, we prefer the legacy translation -- a returning user will
// recognise the wording, and the legacy translator's choice is the one
// vetted by a native speaker.

export default {
  // --- Splash overlay -----------------------------------------------------
  'splash.tapToStart':                 '\u70b9\u51fb\u5f00\u59cb',  // 点击开始

  // --- Orientation prompt -------------------------------------------------
  'orientationPrompt.rotateToLandscape': '\u8bf7\u5c06\u8bbe\u5907\u65cb\u8f6c\u4e3a\u6a2a\u5c4f', // 请将设备旋转为横屏
  'orientationPrompt.widenBrowser':      '\u8bf7\u52a0\u5bbd\u6d4f\u89c8\u5668\u7a97\u53e3',     // 请加宽浏览器窗口

  // --- Home screen --------------------------------------------------------
  // Title kept as the brand "Ewe Can Count" -- legacy iOS shipped image
  // assets for this, not localized text, and the brand is the same in
  // every market.  Learn/Quiz buttons were images in iOS too; web ports
  // them to text and we'll get translations later.
  'home.title':                        'Ewe Can Count',
  // Legacy iOS shipped these as image assets at
  // Images/Interface/zh-Hans.lproj/learn-title.png + quiz-title.png;
  // text transcribed from those images.
  'home.button.learn':                 '\u5b66\u4e60\u6a21\u5f0f',  // 学习模式 (Learn mode)
  'home.button.quiz':                  '\u6d4b\u8bd5\u6a21\u5f0f',  // 测试模式 (Test mode)
  'home.button.settings':              '\u8bbe\u7f6e',             // 设置 (kViewName_Options)

  // --- Settings panel: top-level chrome -----------------------------------
  'settings.title':                    '\u8bbe\u7f6e',             // 设置 (kViewName_Options)
  'settings.close':                    '\u5173\u95ed',             // 关闭

  // --- Settings panel: section headings -----------------------------------
  'settings.section.gameplay':         '\u6e38\u620f\u8bbe\u7f6e',     // 游戏设置 (kOptionsHeaderTitle_Gameplay)
  'settings.section.language':         '\u8bed\u8a00',                  // 语言 (kOptionsHeaderTitle_Languages)
  'settings.section.themes':           '\u4e3b\u9898',                  // 主题 (kOptionsHeaderTitle_Themes)
  'settings.section.support':          '\u652f\u6301',                  // 支持 (matches I Hear Ewe support.heading)
  'settings.section.family':           '\u66f4\u591a Ewe \u5e94\u7528', // 更多 Ewe 应用 ("Ewe" kept in Latin with breathing-room spaces per CJK typography conventions)
  'settings.section.privacy':          '\u9690\u79c1',                  // 隐私
  'settings.section.credits':          '\u9e23\u8c22',                  // 鸣谢 (idiomatic for film/app credits)
  'settings.section.about':            '\u5173\u4e8e',                  // 关于 (kOptionsHeaderTitle_About)

  // --- Gameplay section ---------------------------------------------------
  'settings.maxCount':                       '\u6570\u91cf\u9650\u5236',   // 数量限制 (kOptionsTitle_MaxObjects)
  'settings.entranceAnimations':             '\u51fa\u573a\u52a8\u753b',   // 出场动画 (kOptionsTitle_EnableAnimation)
  'settings.verbalPrompts':                  '\u8bed\u97f3\u63d0\u793a',   // 语音提示 (kOptionsTitle_EnableVerbalPrompts)
  'settings.verbalPromptMode.all':           '\u5168\u90e8',               // 全部 (all prompts)
  'settings.verbalPromptMode.counting-only': '\u4ec5\u6570\u6570',         // 仅数数 (only counting; 数数 = kids' verb "to count out")
  'settings.verbalPromptMode.off':           '\u5173\u95ed',               // 关闭 (off)
  'settings.fullscreen':                     '\u5168\u5c4f',               // 全屏 (web-only; standard CN UI term)
  // iOS fullscreen hint: uses Apple's official iOS Safari labels
  // verbatim ("共享" share-sheet, "添加到主屏幕" Add to Home Screen) so
  // the user's reading matches what they see in Safari.
  'settings.fullscreen.iosHint':
    '\u8981\u5728 iPhone \u6216 iPad \u4e0a\u5168\u5c4f\u4f7f\u7528 Ewe Can Count\uff0c\u8bf7\u6253\u5f00 Safari \u7684\u201c\u5171\u4eab\u201d\u83dc\u5355\u5e76\u9009\u62e9\u201c\u6dfb\u52a0\u5230\u4e3b\u5c4f\u5e55\u201d\u3002\u4ece\u4e3b\u5c4f\u5e55\u56fe\u6807\u542f\u52a8\u53ef\u9690\u85cf\u6d4f\u89c8\u5668\u754c\u9762\u3002',
  // 要在 iPhone 或 iPad 上全屏使用 Ewe Can Count，请打开 Safari 的"共享"菜单并选择"添加到主屏幕"。从主屏幕图标启动可隐藏浏览器界面。
  // iosHint: web-only, no legacy equivalent.  Falls back to English.

  // --- Language section ---------------------------------------------------
  'settings.language':                 '\u8bed\u8a00',                                   // 语言
  // settings.language.locale.* keys are intentionally OMITTED from non-en
  // dictionaries so the resolver falls back to en.js, which holds the
  // *endonym* for each locale (English / 简体中文 / 日本語 / 한국어).
  // Language-picker entries should always read in the language they
  // represent regardless of the active UI locale -- a returning Chinese
  // user shouldn't see "英语" when the English-speaking visitor next to
  // them sees "English".  The legacy iOS strings (kLanguageName_*) used
  // exonyms (each language named in the active locale's vocabulary), but
  // for the web port we follow modern OS picker conventions instead.
  'settings.numeralScript':            '\u6570\u5b57\u7c7b\u578b',                        // 数字类型 (kOptionsHeaderTitle_LanguageScriptOptions)
  'settings.numeralScript.arabic':     '\u963f\u62c9\u4f2f\u6570\u5b57',                  // 阿拉伯数字 (kScriptName_en)
  'settings.numeralScript.chinese':    '\u7b80\u4f53\u4e2d\u6587\u6570\u5b57',            // 简体中文数字 (kScriptName_zh-Hans)
  'settings.numeralScript.japanese':   '\u65e5\u8bed\u6570\u5b57',                        // 日语数字 (kScriptName_ja)
  'settings.numeralScript.sino-korean':  '\u4e2d\u97e9\u6570\u5b57',                       // 中韩数字 (kScriptName_ko-sino)
  'settings.numeralScript.native-korean': '\u97e9\u5f0f\u6570\u5b57',                       // 韩式数字 (kScriptName_ko-hangul)

  // --- Support section ----------------------------------------------------
  // Section copy uses "支持" (support) rather than "捐赠/捐款" (donation)
  // for a friendlier, lower-pressure tone that matches the en wording
  // refresh.  Donate button mirrors I Hear Ewe's Phase 7 Support copy
  // (src/data/locales/zh-Hans.json -> "support.buttonLabel": "用$2支持
  // I Hear Ewe").  We keep the literal "$2" suggested-amount marker and
  // swap the app name to "Ewe Can Count" so siblings stay consistent.
  'settings.support.heading':          '\u652f\u6301 Ewe Can Count',
  'settings.support.description':
    'Ewe Can Count \u514d\u8d39\u4e14\u65e0\u5e7f\u544a\u3002\u5982\u679c\u60a8\u559c\u6b22\uff0c\u4e00\u70b9\u70b9\u652f\u6301\u53ef\u4ee5\u5e2e\u52a9\u652f\u4ed8\u670d\u52a1\u5668\u548c\u5f00\u53d1\u7684\u8d39\u7528\u3002',
  'settings.support.donate':           '\u7528$2\u652f\u6301Ewe Can Count',  // 用$2支持Ewe Can Count
  'settings.support.offline':          '\u8bf7\u8fde\u63a5\u4e92\u8054\u7f51\u4ee5\u8868\u8fbe\u60a8\u7684\u652f\u6301\u3002',
  // Used as alt text on the PayPal wordmark image.
  'settings.support.poweredBy':        '\u7531 PayPal \u63d0\u4f9b',          // 由 PayPal 提供

  // --- Family cross-promo section ----------------------------------------
  'settings.family.body':              'Ewe \u7cfb\u5217\u4e2d\u7684\u5176\u4ed6\u513f\u7ae5\u5e94\u7528\uff1a',
  'settings.family.offline':           '\u8bf7\u8fde\u63a5\u4e92\u8054\u7f51\u4ee5\u6d4f\u89c8\u66f4\u591a Ewe \u5e94\u7528\u3002',
  'family.iHearEwe.tagline':           '\u70b9\u51fb\u8046\u542c\u52a8\u7269\u548c\u65e5\u5e38\u751f\u6d3b\u7684\u58f0\u97f3\u3002',
  'family.eweCanCount.tagline':        '\u548c\u53ef\u7231\u7684\u519c\u573a\u52a8\u7269\u4e00\u8d77\u6570\u6570\u3002',

  // --- Privacy section ----------------------------------------------------
  // disclosurePrefix is rendered followed by the viewStats link (see
  // src/ui/settings-panel.js); a single trailing space provides
  // breathing room before the link arrow.
  'settings.privacy.shareStats':       '\u5171\u4eab\u533f\u540d\u4f7f\u7528\u6570\u636e',
  'settings.privacy.disclosurePrefix':
    '\u672c\u5e94\u7528\u4e0d\u6536\u96c6\u4efb\u4f55\u4e2a\u4eba\u6570\u636e \u2014 \u6240\u6709\u8fdb\u5ea6\u90fd\u4fdd\u5b58\u5728\u60a8\u7684\u8bbe\u5907\u4e0a\u3002\u5982\u679c\u5f00\u542f\u6b64\u5f00\u5173\uff0c\u5c06\u53d1\u9001\u533f\u540d\u6c47\u603b\u8ba1\u6570\uff08\u4f8b\u5982\u201c\u7b54\u5bf9\u4e00\u9898\u201d\uff09\u4ee5\u5e2e\u52a9\u6539\u8fdb\u672c\u5e94\u7528\u3002 ',
  'settings.privacy.viewStats':        '\u67e5\u770b\u7edf\u8ba1\u9875\u9762 \u2192',

  // --- Themes section -----------------------------------------------------
  'settings.themes.hint':              '\u70b9\u51fb\u4e3b\u9898\u4ee5\u5f00\u542f\u6216\u5173\u95ed\u3002\u81f3\u5c11\u9700\u8981\u4fdd\u7559\u4e00\u4e2a\u4e3b\u9898\u3002',
  'settings.themes.minOneTooltip':     '\u81f3\u5c11\u9700\u8981\u4fdd\u7559\u4e00\u4e2a\u4e3b\u9898',
  'settings.theme.sheep':              '\u7ef5\u7f8a',  // 绵羊 (kThemeTitle_sheep)
  'settings.theme.cows':               '\u5976\u725b',  // 奶牛 (kThemeTitle_cows)
  'settings.theme.horses':             '\u9a6c',        // 马   (kThemeTitle_horses)
  'settings.theme.chicks':             '\u5c0f\u9e21',  // 小鸡 (kThemeTitle_chicks)
  'settings.theme.apples':             '\u82f9\u679c',  // 苹果 (kThemeTitle_apples)
  'settings.theme.flowers':            '\u82b1\u6735',  // 花朵 (kThemeTitle_flowers)
};
